/**
 * `t()` for code that is NOT inside a component setup — Pinia stores, above all.
 *
 * `useI18n()` is a vue-i18n *composable*: it reads the current component instance, and throws
 * `SyntaxError: Must be called at the top of a setup function` when there is none. A Pinia setup
 * store looks like a setup function and is not one — it runs the first time the store is resolved,
 * which for `useWorkflowRunsStore` is whichever caller happens to touch it first. That caller is
 * routinely an async callback (a poll tick, a settle listener, a chat card's run handler), and the
 * throw there surfaces as an unhandled rejection with no stack pointing at the store.
 *
 * So the lookup is deferred to the first CALL and tried two ways:
 *
 *   1. `useI18n()` — correct inside a component, and the shape the hermetic tests stub globally.
 *   2. `useNuxtApp().$i18n` — the global composer @nuxtjs/i18n injects, reachable anywhere in the
 *      Nuxt app, including from a timer.
 *
 * Both are called as BARE identifiers on purpose: that is what Nuxt's auto-import transform rewrites
 * at build time, and what falls through to the global stub under vitest, where neither auto-import
 * nor Nuxt exists. Neither available (a store exercised outside Nuxt entirely) echoes the key, which
 * keeps a missing translation cosmetic rather than a crash on a background tick.
 *
 * The resolved function is cached — both candidates return the *composer's* `t`, which tracks the
 * active locale itself, so caching does not pin the language.
 */
export type TranslateFn = (key: string, params?: any) => string

export function useLazyT(): TranslateFn {
  let resolved: TranslateFn | null = null

  return (key: string, params?: any): string => {
    if (!resolved) resolved = resolveTranslate()
    if (!resolved) return key
    return params === undefined ? resolved(key) : resolved(key, params)
  }
}

function resolveTranslate(): TranslateFn | null {
  return fromComposable() ?? fromNuxtApp()
}

function fromComposable(): TranslateFn | null {
  try {
    const t = (useI18n as any)?.()?.t
    return typeof t === 'function' ? t : null
  } catch {
    // The expected path outside a setup function. Fall through to the Nuxt-app composer.
    return null
  }
}

function fromNuxtApp(): TranslateFn | null {
  try {
    const t = (useNuxtApp as any)?.()?.$i18n?.t
    return typeof t === 'function' ? t : null
  } catch {
    return null
  }
}
